/**
 * Low-level API client functions for Benefit Classes
 *
 * These functions handle the actual HTTP requests to the BTX service.
 * They are consumed by React Query hooks in mutations.ts and queries.ts.
 */

import { AxiosInstance } from "axios";
import { logger } from "@platform/browser-logger";

const AXIOS_INSTANCE_REQUIRED_ERROR =
  "Axios instance required for benefit classes API";

export interface CoverageResponse {
  id: string;
  type: string;
  createdAt: string;
  updatedAt: string;
}

export interface BenefitClassResponse {
  id: string;
  name: string;
  rfpId: string;
  coverageIds: string[];
  coverages?: CoverageResponse[];
  benefitPlans?: string[];
}

export interface BatchUpdateBenefitClassesRequest {
  updates: {
    id: string;
    name: string;
    coverageIds: string[];
  }[];
}

export interface CreateBenefitClassRequest {
  rfpId: string;
  benefitClass: {
    name: string;
    coverageIds?: string[];
  };
}

/**
 * Lists all benefit classes for an RFP with optional coverage details
 *
 * Uses BTX endpoint: GET /api/v1/benefit-classes?rfpId={rfpId}&include=coverages
 *
 * @param rfpId - The RFP ID to fetch benefit classes for
 * @param includeCoverages - Whether to include full coverage details
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to array of benefit classes
 *
 * @example
 * ```ts
 * const classes = await listBenefitClasses("rfp-123", true, axiosInstance);
 * console.log(classes[0].coverages);
 * ```
 */
export const listBenefitClasses = async (
  rfpId: string,
  includeCoverages: boolean = true,
  axiosInstance?: AxiosInstance,
): Promise<BenefitClassResponse[]> => {
  if (!axiosInstance) {
    throw new Error(AXIOS_INSTANCE_REQUIRED_ERROR);
  }

  const params: Record<string, string> = {
    rfpId,
  };

  if (includeCoverages) {
    params.include = "coverages";
  }

  logger.debug("[BTX API] Listing benefit classes:", {
    rfpId,
    includeCoverages,
  });

  const response = await axiosInstance.get<BenefitClassResponse[]>(
    "/btx/v1/benefit-classes",
    { params },
  );

  logger.debug("[BTX API] Listed benefit classes:", {
    rfpId,
    count: response.data.length,
  });

  return response.data;
};

/**
 * Creates a new benefit class for an RFP
 *
 * Uses BTX endpoint: POST /api/v1/benefit-classes
 *
 * @param request - Create benefit class request with RFP ID and class details
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to created benefit class
 *
 * @example
 * ```ts
 * const newClass = await createBenefitClass({
 *   rfpId: "rfp-123",
 *   benefitClass: { name: "Class F", coverageIds: [] }
 * }, axiosInstance);
 * ```
 */
export const createBenefitClass = async (
  request: CreateBenefitClassRequest,
  axiosInstance?: AxiosInstance,
): Promise<BenefitClassResponse> => {
  if (!axiosInstance) {
    throw new Error(AXIOS_INSTANCE_REQUIRED_ERROR);
  }

  logger.info("[BTX API] Creating benefit class:", {
    rfpId: request.rfpId,
    name: request.benefitClass.name,
  });

  const response = await axiosInstance.post<BenefitClassResponse>(
    "/btx/v1/benefit-classes",
    request,
  );

  logger.info("[BTX API] Created benefit class:", {
    classId: response.data.id,
    name: response.data.name,
  });

  return response.data;
};

/**
 * Batch updates benefit classes and their coverage associations
 *
 * Uses BTX endpoint: PATCH /api/v1/benefit-classes/batch
 *
 * @param request - Batch update request with array of updates
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to updated benefit classes
 *
 * @example
 * ```ts
 * const updated = await batchUpdateBenefitClasses({
 *   updates: [
 *     { id: "class-1", name: "Class A", coverageIds: ["cov-1", "cov-2"] },
 *     { id: "class-2", name: "Class B", coverageIds: ["cov-1"] }
 *   ]
 * }, axiosInstance);
 * ```
 */
export const batchUpdateBenefitClasses = async (
  request: BatchUpdateBenefitClassesRequest,
  axiosInstance?: AxiosInstance,
): Promise<BenefitClassResponse[]> => {
  if (!axiosInstance) {
    throw new Error(AXIOS_INSTANCE_REQUIRED_ERROR);
  }

  logger.info("[BTX API] Batch updating benefit classes:", {
    count: request.updates.length,
  });

  const response = await axiosInstance.patch<BenefitClassResponse[]>(
    "/btx/v1/benefit-classes/batch",
    request,
  );

  logger.info("[BTX API] Batch updated benefit classes:", {
    count: response.data.length,
  });

  return response.data;
};

/**
 * Deletes a benefit class by ID
 *
 * Uses BTX endpoint: DELETE /api/v1/benefit-classes/:id
 *
 * @param classId - The benefit class ID to delete
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving when deletion is complete
 *
 * @example
 * ```ts
 * await deleteBenefitClass("class-1", axiosInstance);
 * ```
 */
export const deleteBenefitClass = async (
  classId: string,
  axiosInstance?: AxiosInstance,
): Promise<void> => {
  if (!axiosInstance) {
    throw new Error(AXIOS_INSTANCE_REQUIRED_ERROR);
  }

  logger.info("[BTX API] Deleting benefit class:", { classId });

  await axiosInstance.delete(`/btx/v1/benefit-classes/${classId}`);

  logger.info("[BTX API] Deleted benefit class:", { classId });
};
